<template>
  <div class="mobile-listing">
    <transition name="fade" mode="out-in">
      <div class="validation-section" v-if="!isCustomerVerified" :key="'verification'">
        <h2 class="title">{{ $t('listingPage.validationTitle') }}</h2>
        <p class="description" v-html="verificationDescription"></p>
        <base-button variant="text"
                     @click="sendOtpCode"
                     :disabled="isOtpCodeBeingSent">
          {{ isOtpCodeSent ? $t('listingPage.sendOtpCodeAgain') : $t('listingPage.sendOtpCode') }}
        </base-button>

        <validation-observer ref="otpCodeObs"
                             tag="form"
                             class="verification-form"
                             v-slot="{ errors }"
                             @submit.prevent="submitOtpCodeVerification">
          <base-v-input v-model.trim="otpCode"
                        id="otpCode"
                        name="otpCode"
                        rules="required|otpCode"
                        variant="white"
                        :submitted="otpCodeSubmitted"
                        :label="$t('registration.codeLabel')"
                        vid="otpCode"
                        maxlength="20"/>
          <div class="form-actions">
            <base-button :disabled="!otpCode.length || !!errors.otpCode.length || !isOtpCodeSent"
                         :loading="dataLoading"
                         class="continue-btn"
                         type="submit">
              {{ $t('common.confirm') }}
            </base-button>
          </div>
        </validation-observer>
      </div>
      <div class="listing-section" v-else :key="'listing'">
        <h2 class="title">{{ $t('listingPage.listingTitle') }}</h2>
        <listing-table :is-prepaid="isPrepaid"
                       :data="historyData"
                       :total-expense="totalExpense"
                       @select-month="getHistory"/>
      </div>
    </transition>
  </div>
</template>

<script>
import errorMessageHandler from '@/mixins/errorMessageHandler'
import { makeQueryString } from '@/utils/queryParser'
import ListingTable from '@/components/services/resources/listing-table'
import { getApiResponse } from '@/services/apiResponsesDictionary'
import selectionBlockEvents from '@/mixins/selectionBlockEvents'

export default {
  name: 'mobile-listing',
  components: { ListingTable },
  data () {
    return {
      isOtpCodeBeingSent: false,
      isOtpCodeSent: false,
      otpCode: '',
      otpCodeSubmitted: false,
      dataLoading: false,
      isCustomerVerified: false,
      historyData: [],
      totalExpense: '0.00€',
      tempTimer: null
    }
  },
  props: {
    resourcesData: Object
  },
  mixins: [errorMessageHandler, selectionBlockEvents],
  computed: {
    verificationDescription () {
      const phone = { phoneNumber: this.$props.resourcesData.headerTitle }
      return this.isOtpCodeSent ? this.$t('listingPage.validationDescriptionSent', phone) : this.$t('listingPage.validationDescriptionNotSent', phone)
    },
    isPrepaid () {
      return this.$props.resourcesData && this.$props.resourcesData.subscriptionType === 'MOBILE_PREPAID'
    }
  },
  methods: {
    sendOtpCode () {
      this.isOtpCodeBeingSent = true
      const dataToSend = {
        msisdn: this.$props.resourcesData.subscriberIdentity,
        platform: 'WEB',
        otpRequestType: 'LISTING'
      }
      this.$apiRequest.post('user/sendotpcode', dataToSend)
        .then(response => {
          this.isOtpCodeSent = true
          this.toast('success', {
            message: this.$t('toastMessages.otpCodeSent')
          })
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.isOtpCodeBeingSent = false
        })
    },
    async submitOtpCodeVerification () {
      const isFormValid = await this.$refs.otpCodeObs.validate()
      this.otpCodeSubmitted = true
      if (isFormValid) {
        this.verifyOtpCode()
      }
    },
    verifyOtpCode () {
      const dataToSend = {
        msisdn: this.$props.resourcesData.subscriberIdentity,
        otp: this.otpCode,
        platform: 'WEB',
        otpRequestType: 'LISTING'
      }
      this.dataLoading = true
      this.$apiRequest.post('user/verifyotpcode', dataToSend)
        .then(response => {
          this.resetFormData()

          this.isCustomerVerified = true

          this.startCountdownTimer()

          // get data
          const currentMonth = this.getCurrentMonth()
          this.getHistory(currentMonth)
        })
        .catch(e => {
          // TODO Remove if condition when BE message is fixed, leave only what's in else
          if (e.response.data.errors.length && e.response.data.errors[0].type === 'ValidationError') {
            this.toast('error', {
              message: getApiResponse('verifyOtpCode', 'MtmInvalidOtpCodeError')
            })
          } else {
            this.handleErrorMessage(e.response.data)
          }
        })
        .finally(() => {
          this.dataLoading = false
        })
    },
    getHistory (month) {
      const apiProps = {
        msisdn: this.$props.resourcesData.subscriberIdentity
      }

      if (month) {
        apiProps.month = month
      }

      const qs = makeQueryString(apiProps)
      this.$apiRequest.get(`user/listing${qs}`)
        .then(response => {
          this.historyData = response.data.entries
          this.totalExpense = response.data.total
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)

          if (e.response.data.errors && e.response.data.errors[0].type === 'MtmOtpCodeExpiredError') {
            this.isCustomerVerified = false
          }
        })
    },
    getCurrentMonth () {
      const monthOffset = this.isPrepaid ? 0 : 1
      let currentMonth = new Date().getMonth() + 1 - monthOffset

      if (currentMonth < 1) currentMonth = 12

      return currentMonth
    },
    resetFormData () {
      this.otpCodeSubmitted = false
      this.otpCode = ''
    },
    startCountdownTimer () {
      this.tempTimer = setTimeout(() => {
        this.isCustomerVerified = false
        this.clearCountdownTimer()
      }, 10 * 60 * 1000)
    },
    clearCountdownTimer () {
      clearTimeout(this.tempTimer)
      this.tempTimer = null
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.unblockSidebarSelection(false)
    })
  },
  beforeDestroy () {
    this.clearCountdownTimer()
  }
}
</script>

<style scoped lang="scss">
.mobile-listing {
  color: $text;
  padding-top: 2rem;

  .validation-section, .listing-section {
    .title {
      font-size: 2.4rem;
      font-weight: normal;
      text-transform: uppercase;
      padding-bottom: 0.8rem;
      border-bottom: 1px solid $form-border-color;
      margin-bottom: 2.4rem;
    }

    .description {
      font-size: 1.6rem;
      width: 100%;
      max-width: 480px;
    }

    .verification-form {
      padding-top: 2.4rem;
      width: 100%;
      max-width: 330px;
    }
  }
}
</style>
